import React from "react";
import { motion } from "framer-motion";
import { Container } from "./Container";
import { Eyebrow } from "./Eyebrow";
import { Heading } from "./Heading";
import { Button } from "./Button";

/**
 * Closing CTA section — full-width band with subtle radial green glow.
 * Reused across most pages.
 */
export const ClosingCTA = ({
    eyebrow = "Let's Talk",
    title = "Let's build something enduring.",
    subtitle = "Whether you're expanding your engineering team, seeking partnership, or exploring strategic advisory — we're ready to talk.",
    primary = { label: "Contact Us", to: "/contact" },
    secondary = { label: "Explore Divisions", to: "/divisions" },
    "data-testid": testId = "closing-cta",
}) => {
    return (
        <section
            className="relative isolate overflow-hidden border-t border-border-subtle"
            data-testid={testId}
        >
            <div className="absolute inset-0 -z-10">
                <div
                    aria-hidden
                    className="absolute left-1/2 top-1/2 h-[600px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full"
                    style={{
                        background:
                            "radial-gradient(ellipse at center, rgba(124,255,79,0.10) 0%, rgba(124,255,79,0.03) 35%, transparent 65%)",
                        filter: "blur(60px)",
                    }}
                />
                <div className="zen-grid opacity-50" />
            </div>
            <Container>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="mx-auto max-w-3xl py-32 text-center lg:py-40"
                >
                    <Eyebrow withDot>{eyebrow}</Eyebrow>
                    <Heading as="h2" className="mt-6">
                        {title}
                    </Heading>
                    <p className="mx-auto mt-6 max-w-xl text-[17px] leading-[1.65] text-text-secondary">
                        {subtitle}
                    </p>
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                        {primary && (
                            <Button
                                to={primary.to}
                                variant="primary"
                                size="lg"
                                data-testid={`${testId}-primary`}
                            >
                                {primary.label}
                            </Button>
                        )}
                        {secondary && (
                            <Button
                                to={secondary.to}
                                variant="ghost"
                                size="lg"
                                data-testid={`${testId}-secondary`}
                            >
                                {secondary.label}
                            </Button>
                        )}
                    </div>
                </motion.div>
            </Container>
        </section>
    );
};

export default ClosingCTA;
